import Phaser from 'phaser';
import type { ScoreEntry } from '@platoon/netcore';
import { BRASS, INK, INK_DIM, hudText } from './text';

// Hold-to-view scoreboard (Tab), in the tradition of MoH:AA / Spearhead:
// a centred translucent panel over the running game, one block per team
// (a single block in deathmatch), rows sorted by kills, own row highlighted.
// Data comes from the server's MSG_SCORES roster once a second.

const ROW_H = 22;
const PAD = 16;
const MAX_WIDTH = 720;

// Default team colours and names by team id. 0 = no team (free-for-all).
export const DEFAULT_TEAM_COLORS: Readonly<Record<number, number>> = { 0: 0x9fb09a, 1: 0x5b8fd6, 2: 0xd65b5b, 3: 0x6fbf73, 4: 0xd6b25b };
export const DEFAULT_TEAM_NAMES: Readonly<Record<number, string>> = { 0: 'Players', 1: 'Blue', 2: 'Red', 3: 'Green', 4: 'Gold' };

export interface ScoreboardOptions {
  title: string; // e.g. the game's name
  onOptions: () => void; // ☰ Menu
  onInvite: (roomId: string) => void; // 🔗 Invite, when a room id is set
  onStart?: () => void; // the "Start now" button (warm-up), when info.startLabel is set
  teamColors?: Readonly<Record<number, number>>;
  teamNames?: Readonly<Record<number, string>>;
}

export interface ScoreboardInfo {
  levelName: string;
  mode: string;
  roomId?: string; // shown in the title line; the Invite button copies its link
  // Intermission: headline ("MATCH OVER") and detail ("Yiannis wins · next
  // match in 7"). Rendered above the table when set.
  banner?: string;
  bannerDetail?: string;
  startLabel?: string; // warm-up: a button under the banner
  // Warm-up / team rooms: draw these teams even when empty, and pad each
  // with "waiting for a player" rows up to slotsPerTeam.
  teamIds?: readonly number[];
  slotsPerTeam?: number;
}

export class Scoreboard {
  private readonly container: Phaser.GameObjects.Container;
  private entries: ScoreEntry[] = [];
  private myId = -1;
  private info: ScoreboardInfo = { levelName: '', mode: '' };
  private dirty = true;

  private readonly teamColors: Readonly<Record<number, number>>;
  private readonly teamNames: Readonly<Record<number, string>>;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly opts: ScoreboardOptions,
  ) {
    this.teamColors = opts.teamColors ?? DEFAULT_TEAM_COLORS;
    this.teamNames = opts.teamNames ?? DEFAULT_TEAM_NAMES;
    this.container = scene.add.container(0, 0).setDepth(200).setVisible(false);
  }

  setData(entries: ScoreEntry[], myId: number, info: ScoreboardInfo): void {
    this.entries = entries;
    this.myId = myId;
    this.info = info;
    this.dirty = true;
  }

  get visible(): boolean {
    return this.container.visible;
  }

  setVisible(v: boolean): void {
    if (v && (this.dirty || !this.container.visible)) this.rebuild();
    this.container.setVisible(v);
  }

  // Called on resize; cheap when hidden.
  relayout(): void {
    this.dirty = true;
    if (this.container.visible) this.rebuild();
  }

  private rebuild(): void {
    this.dirty = false;
    this.container.removeAll(true);

    const sw = this.scene.scale.width;
    const sh = this.scene.scale.height;
    const width = Math.min(MAX_WIDTH, sw - 2 * PAD);

    // Group by team, sort each block by kills desc then deaths asc.
    const byTeam = new Map<number, ScoreEntry[]>();
    for (const e of this.entries) {
      let list = byTeam.get(e.team);
      if (!list) byTeam.set(e.team, (list = []));
      list.push(e);
    }
    for (const t of this.info.teamIds ?? []) if (!byTeam.has(t)) byTeam.set(t, []);
    const teams = [...byTeam.keys()].sort((a, b) => a - b);
    // Humans first, then bots, each by kills desc then deaths asc.
    for (const list of byTeam.values()) list.sort((a, b) => (a.flags & 1) - (b.flags & 1) || b.kills - a.kills || a.deaths - b.deaths);
    const rowsOf = (t: number): number => Math.max(byTeam.get(t)!.length, this.info.slotsPerTeam ?? 0);

    // Measure first so the panel can be vertically centred.
    const bannerH = this.info.banner ? (this.info.startLabel ? 92 : 58) : 0;
    let height = PAD + 28 + 8 + bannerH; // title (+ banner)
    for (const t of teams) height += 26 + ROW_H * rowsOf(t) + 10;
    height += PAD;
    const x0 = (sw - width) / 2;
    const y0 = Math.max(PAD, (sh - height) / 2);

    const bg = this.scene.add.graphics();
    bg.fillStyle(0x070a08, 0.82).fillRoundedRect(x0, y0, width, height, 6);
    bg.lineStyle(2, BRASS, 1).strokeRoundedRect(x0, y0, width, height, 6);
    this.container.add(bg);

    // Title line: game · level · mode · gear (opens the options dialog)
    this.container.add(this.text(x0 + PAD, y0 + PAD, this.opts.title, 18, '#e8d7a8'));
    const gear = this.text(x0 + width - PAD, y0 + PAD - 2, '☰ Menu', 16, '#e8d7a8')
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true })
      .on(Phaser.Input.Events.POINTER_OVER, () => gear.setColor('#ffffff'))
      .on(Phaser.Input.Events.POINTER_OUT, () => gear.setColor('#e8d7a8'))
      .on(Phaser.Input.Events.POINTER_DOWN, () => this.opts.onOptions());
    this.container.add(gear);
    let right = gear.x - gear.width - 16;
    if (this.info.roomId) {
      const roomId = this.info.roomId;
      const invite = this.text(right, y0 + PAD - 2, '🔗 Invite', 16, '#e8d7a8')
        .setOrigin(1, 0)
        .setInteractive({ useHandCursor: true })
        .on(Phaser.Input.Events.POINTER_OVER, () => invite.setColor('#ffffff'))
        .on(Phaser.Input.Events.POINTER_OUT, () => invite.setColor('#e8d7a8'))
        .on(Phaser.Input.Events.POINTER_DOWN, () => this.opts.onInvite(roomId));
      this.container.add(invite);
      right = invite.x - invite.width - 16;
    }
    const room = this.info.roomId ? `   room ${this.info.roomId}` : '';
    this.container.add(this.text(right, y0 + PAD + 3, `${this.info.levelName}   ${this.info.mode}${room}`, 14, INK_DIM).setOrigin(1, 0));

    // Column anchors (right-aligned numeric columns).
    const colName = x0 + PAD;
    const colPing = x0 + width - PAD;
    const colDeaths = colPing - 80;
    const colKills = colDeaths - 90;

    let y = y0 + PAD + 28 + 8;
    if (this.info.banner) {
      const rule = this.scene.add.graphics();
      rule.lineStyle(1, BRASS, 0.6).lineBetween(x0 + PAD, y, x0 + width - PAD, y);
      this.container.add(rule);
      this.container.add(this.text(x0 + width / 2, y + 8, this.info.banner, 22, '#e8d7a8').setOrigin(0.5, 0));
      if (this.info.bannerDetail) {
        this.container.add(this.text(x0 + width / 2, y + 36, this.info.bannerDetail, 14, INK).setOrigin(0.5, 0));
      }
      if (this.info.startLabel) {
        const label = this.info.startLabel;
        const btnBg = this.scene.add.graphics();
        const bw = 180;
        btnBg.fillStyle(0x2a2318, 1).fillRoundedRect(x0 + width / 2 - bw / 2, y + 58, bw, 28, 4);
        btnBg.lineStyle(1, BRASS, 1).strokeRoundedRect(x0 + width / 2 - bw / 2, y + 58, bw, 28, 4);
        this.container.add(btnBg);
        const btn = this.text(x0 + width / 2, y + 63, label, 15, '#e8d7a8')
          .setOrigin(0.5, 0)
          .setInteractive({ useHandCursor: true })
          .on(Phaser.Input.Events.POINTER_OVER, () => btn.setColor('#ffffff'))
          .on(Phaser.Input.Events.POINTER_OUT, () => btn.setColor('#e8d7a8'))
          .on(Phaser.Input.Events.POINTER_DOWN, () => this.opts.onStart?.());
        this.container.add(btn);
      }
      y += bannerH;
    }
    for (const team of teams) {
      const list = byTeam.get(team)!;
      const color = this.teamColors[team] ?? 0xffffff;
      const name = this.teamNames[team] ?? `Team ${team}`;
      const teamKills = list.reduce((s, e) => s + e.kills, 0);

      // Team header bar
      const bar = this.scene.add.graphics();
      bar.fillStyle(color, 0.25).fillRect(x0 + 6, y, width - 12, 24);
      bar.fillStyle(color, 1).fillRect(x0 + 6, y, 4, 24);
      this.container.add(bar);
      this.container.add(this.text(colName + 4, y + 4, team === 0 ? name : `${name}   ${teamKills}`, 14, INK));
      this.container.add(this.text(colKills, y + 5, 'Kills', 12, INK_DIM).setOrigin(1, 0));
      this.container.add(this.text(colDeaths, y + 5, 'Deaths', 12, INK_DIM).setOrigin(1, 0));
      this.container.add(this.text(colPing, y + 5, 'Ping', 12, INK_DIM).setOrigin(1, 0));
      y += 26;

      for (const e of list) {
        if (e.id === this.myId) {
          const hl = this.scene.add.graphics();
          hl.fillStyle(0xffffff, 0.1).fillRect(x0 + 6, y, width - 12, ROW_H);
          this.container.add(hl);
        }
        const bot = (e.flags & 1) !== 0;
        const ink = e.id === this.myId ? '#ffffff' : bot ? INK_DIM : INK;
        this.container.add(this.text(colName + 4, y + 3, (e.name || `Player ${e.id}`) + (bot ? '   · bot, waiting for a player' : ''), 14, ink));
        this.container.add(this.text(colKills, y + 3, String(e.kills), 14, ink).setOrigin(1, 0));
        this.container.add(this.text(colDeaths, y + 3, String(e.deaths), 14, ink).setOrigin(1, 0));
        this.container.add(this.text(colPing, y + 3, String(e.ping), 14, this.pingColor(e.ping)).setOrigin(1, 0));
        y += ROW_H;
      }
      for (let i = list.length; i < (this.info.slotsPerTeam ?? 0); i++) {
        this.container.add(this.text(colName + 4, y + 3, '— open slot, waiting for a player —', 14, INK_DIM));
        y += ROW_H;
      }
      y += 10;
    }
  }

  private pingColor(ms: number): string {
    return ms < 80 ? '#8fd98a' : ms < 160 ? '#e0c060' : '#e07a5a';
  }

  private text(x: number, y: number, s: string, size: number, color: string): Phaser.GameObjects.Text {
    return hudText(this.scene, x, y, s, size, color);
  }
}
